import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { AtSign, ArrowRight, CheckCheck, Loader2, Inbox as InboxIcon, MessageSquare, Folder, Image as ImageIcon, Activity } from 'lucide-react';

/**
 * MessagesInbox — global list of every @mention this user has received,
 * across every channel (project chats, asset chats, task chats, review
 * comments). The "I missed Rahul's message" pain-killer.
 *
 *   Click a row → routes to the originating channel and the row marks
 *   itself seen (also auto-seen when the user opens that channel directly).
 */
export default function MessagesInbox() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get('/mentions/inbox?limit=200');
      setItems(r.data?.items || []);
      setErr('');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load mentions');
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);
  // Light auto-poll while the user is sitting on this page.
  // Perf rewrite May 24, 2026: 8s → 30s + pause when tab hidden.
  useEffect(() => {
    const tick = () => { if (!document.hidden) refresh(); };
    const t = setInterval(tick, 30000);
    const onVis = () => { if (!document.hidden) refresh(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  const markAllSeen = async () => {
    await api.post('/mentions/seen-all', {});
    refresh();
  };

  const openMention = async (m) => {
    // Mark seen optimistically
    setItems(list => list.map(x => x.id === m.id ? { ...x, seen_at: new Date().toISOString() } : x));
    api.post(`/mentions/${m.id}/seen`, {}).catch(() => {});
    // Route by context_type
    if (m.context_type === 'project') {
      navigate(`/messages?project=${m.context_id}`);
    } else if (m.context_type === 'asset') {
      // Open the asset audit-trail modal via a query param the Reviews page reads
      navigate(`/reviews?audit=${m.context_id}`);
    } else if (m.context_type === 'task') {
      navigate(`/production?task=${m.context_id}`);
    } else if (m.context_type === 'submission') {
      navigate(`/reviews?sub=${m.context_id}`);
    } else if (m.context_type === 'dm') {
      navigate(`/messages?dm=${m.context_id}`);
    }
  };

  const unreadCount = items.filter(i => !i.seen_at).length;
  const groups = [
    { key: 'unread', l: 'Unread',  items: items.filter(i => !i.seen_at) },
    { key: 'read',   l: 'Earlier', items: items.filter(i =>  i.seen_at) },
  ];

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 p-6 max-w-3xl mx-auto" data-testid="mentions-inbox">
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
            <AtSign className="h-7 w-7 text-violet-400" /> Mentions
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            Every time someone tags <span className="font-mono text-violet-300">@you</span>, it lands here.
            {unreadCount > 0 && <> <span className="text-amber-300 font-semibold ml-1.5">{unreadCount} unread</span></>}
          </p>
        </div>
        {unreadCount > 0 && (
          <button onClick={markAllSeen} data-testid="mark-all-seen-btn"
            className="h-8 px-3 rounded-md bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-xs text-zinc-300 flex items-center gap-1.5">
            <CheckCheck className="w-3 h-3" /> Mark all read
          </button>
        )}
      </div>

      {loading && (
        <div className="py-16 flex items-center justify-center text-zinc-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
        </div>
      )}
      {err && !loading && (
        <div className="rounded-md bg-rose-500/10 border border-rose-500/40 px-4 py-3 text-xs text-rose-200">
          {err}
        </div>
      )}
      {!loading && !err && items.length === 0 && (
        <div className="rounded-md border border-zinc-800 bg-zinc-900/60 p-12 text-center" data-testid="inbox-empty">
          <InboxIcon className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <p className="text-sm text-zinc-400">You're all caught up.</p>
          <p className="text-[11px] text-zinc-500 mt-1">When someone tags <span className="font-mono">@your-name</span> anywhere, it'll show here instantly.</p>
        </div>
      )}
      {!loading && groups.map(g => g.items.length > 0 && (
        <section key={g.key} className="mb-5" data-testid={`group-${g.key}`}>
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 px-1">{g.l} <span className="font-mono text-zinc-600 ml-1">({g.items.length})</span></p>
          <ul className="space-y-1.5">
            {g.items.map(m => (
              <li key={m.id}>
                <button onClick={() => openMention(m)}
                  data-testid={`mention-row-${m.id}`}
                  className={`w-full text-left px-4 py-3 rounded-md border transition flex items-start gap-3 ${
                    !m.seen_at
                      ? 'bg-violet-500/5 border-violet-500/40 hover:bg-violet-500/10'
                      : 'bg-zinc-900/40 border-zinc-800 hover:bg-zinc-900'
                  }`}>
                  <ContextIcon type={m.context_type} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap text-[11px]">
                      <span className="font-semibold text-violet-300">@you</span>
                      <span className="text-zinc-500">tagged by</span>
                      <span className="text-zinc-200 font-medium">{m.mentioner_name}</span>
                      <span className="text-zinc-600">·</span>
                      <span className="text-zinc-500 font-mono">{fmtAgo(m.created_at)}</span>
                      {!m.seen_at && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-violet-400" />}
                    </div>
                    {m.subject_hint && (
                      <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">{m.subject_hint}</p>
                    )}
                    <p className="text-sm text-zinc-200 mt-1.5 line-clamp-2">{m.snippet}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-zinc-500 flex-shrink-0 mt-2" />
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

const ContextIcon = ({ type }) => {
  const cfg = ({
    project:    { Icon: Folder,        cls: 'text-cyan-300' },
    asset:      { Icon: ImageIcon,     cls: 'text-rose-300' },
    task:       { Icon: Activity,      cls: 'text-amber-300' },
    submission: { Icon: ImageIcon,     cls: 'text-emerald-300' },
    dm:         { Icon: MessageSquare, cls: 'text-violet-300' },
  })[type] || { Icon: AtSign, cls: 'text-zinc-400' };
  const Icon = cfg.Icon;
  return (
    <span className={`h-9 w-9 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center flex-shrink-0 ${cfg.cls}`}>
      <Icon className="w-4 h-4" />
    </span>
  );
};

const fmtAgo = (iso) => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};
